// Adaptador de persistência sobre sql.js. É o único ponto do código que fala
// SQLite. Recebe a Database já aberta (o chamador escolhe o build de sql.js:
// wasm no Node dos testes, asm.js no navegador) e um `persist` que grava os
// bytes onde quer que seja (arquivo local, no app).
//
// O mapeamento entidade <-> tabela mora em `tabelas.ts`; aqui fica só a
// execução do SQL, mais `config` e `lista_valor`, que não são coleções de
// entidades e por isso ficam de fora do mapa.

import type { Database } from "sql.js";
import { migrations } from "../domain/schema.ts";
import type { Repository } from "../domain/ports.ts";
import type { Snapshot } from "../domain/snapshot.ts";
import { emptySnapshot } from "../domain/snapshot.ts";
import {
  colunasDe,
  daLinha,
  MAPEADAS,
  paraLinha,
  TABELAS,
  tabelasParaLimpar,
  type Tabela,
} from "./tabelas.ts";

export type Persist = (bytes: Uint8Array) => Promise<void>;

function userVersion(db: Database): number {
  const rows = db.exec("PRAGMA user_version");
  const v = rows[0]?.values[0]?.[0];
  return typeof v === "number" ? v : 0;
}

/** Aplica as migrações pendentes em ordem, numa transação. Idempotente. */
export function migrate(db: Database): void {
  const from = userVersion(db);
  const pending = migrations
    .filter((m) => m.version > from)
    .sort((a, b) => a.version - b.version);
  if (pending.length === 0) return;

  db.run("BEGIN");
  try {
    for (const m of pending) {
      for (const statement of m.statements) db.run(statement);
    }
    const to = pending[pending.length - 1]!.version;
    db.run(`PRAGMA user_version = ${to}`);
    db.run("COMMIT");
  } catch (err) {
    db.run("ROLLBACK");
    throw err;
  }
}

/** Lê as coleções que estão no mapa (`tabelas.ts`) para dentro do Snapshot. */
function lerMapeadas(db: Database, s: Snapshot): void {
  for (const chave of MAPEADAS) {
    const t = TABELAS[chave] as Tabela<unknown>;
    const cols = colunasDe(t);
    const ordem = t.ordem ? ` ORDER BY ${t.ordem}` : "";
    const alvo = s[chave] as unknown[];
    for (const r of rows(db, `SELECT ${cols.join(", ")} FROM ${t.nome}${ordem}`)) {
      alvo.push(daLinha(t, r));
    }
  }
}

/** Grava as coleções que estão no mapa, na ordem em que ele as declara. */
function gravarMapeadas(db: Database, s: Snapshot): void {
  for (const chave of MAPEADAS) {
    const t = TABELAS[chave] as Tabela<unknown>;
    const cols = colunasDe(t);
    const sql = `INSERT INTO ${t.nome} (${cols.join(", ")}) VALUES (${cols
      .map(() => "?")
      .join(", ")})`;
    for (const item of s[chave] as unknown[]) db.run(sql, paraLinha(t, item));
  }
}

export function loadSnapshot(db: Database): Snapshot {
  const s = emptySnapshot();
  lerMapeadas(db, s);

  {
    const raw = rows(db, "SELECT valor FROM config WHERE chave='rodapeExport'")[0];
    if (raw) {
      try {
        s.config.rodapeExport = JSON.parse(str(raw["valor"])) as {
          slogan: string;
          whatsapp: string;
        };
      } catch {
        /* mantém o default */
      }
    }
  }
  {
    const raw = rows(
      db,
      "SELECT valor FROM config WHERE chave='dataFaturamento'",
    )[0];
    if (raw && (str(raw["valor"]) === "coleta" || str(raw["valor"]) === "devolucao")) {
      s.config.dataFaturamento = str(raw["valor"]) as "coleta" | "devolucao";
    }
  }
  {
    // o antigo "diasPadraoDashboard" (em dias) é descartado: o período agora é em meses (ADR-0032)
    const raw = rows(db, "SELECT valor FROM config WHERE chave='mesesPadraoDashboard'")[0];
    const n = raw ? Number(raw["valor"]) : NaN;
    if (Number.isInteger(n) && n >= 0) s.config.mesesPadraoDashboard = n;
  }
  {
    const raw = rows(db, "SELECT valor FROM config WHERE chave='volumeMinimoRanking'")[0];
    const n = raw ? Number(raw["valor"]) : NaN;
    if (Number.isInteger(n) && n > 0) s.config.volumeMinimoRanking = n;
  }

  for (const r of rows(
    db,
    "SELECT tipo, valor FROM lista_valor ORDER BY tipo, ordem",
  )) {
    const tipo = str(r["tipo"]);
    if (
      tipo === "funcoesFuncionario" ||
      tipo === "categoriasInsumo" ||
      tipo === "unidadesInsumo" ||
      tipo === "tiposCliente" ||
      tipo === "processosPeca" ||
      tipo === "motivosRetencao" ||
      tipo === "motivosDivergencia"
    ) {
      s.listas[tipo].push(str(r["valor"]));
    }
  }
  return s;
}

export function writeSnapshot(db: Database, s: Snapshot): void {
  db.run("BEGIN");
  try {
    db.run(tabelasParaLimpar().map((t) => `DELETE FROM ${t}`).join("; "));
    gravarMapeadas(db, s);
    db.run("INSERT INTO config (chave, valor) VALUES ('rodapeExport', ?)", [
      JSON.stringify(s.config.rodapeExport),
    ]);
    db.run("INSERT INTO config (chave, valor) VALUES ('dataFaturamento', ?)", [
      s.config.dataFaturamento,
    ]);
    db.run("INSERT INTO config (chave, valor) VALUES ('mesesPadraoDashboard', ?)", [
      s.config.mesesPadraoDashboard,
    ]);
    db.run("INSERT INTO config (chave, valor) VALUES ('volumeMinimoRanking', ?)", [
      s.config.volumeMinimoRanking,
    ]);
    for (const [tipo, valores] of Object.entries(s.listas) as [
      string,
      string[],
    ][]) {
      valores.forEach((valor, ordem) => {
        db.run(
          "INSERT INTO lista_valor (tipo, valor, ordem) VALUES (?, ?, ?)",
          [tipo, valor, ordem],
        );
      });
    }
    db.run("COMMIT");
  } catch (err) {
    db.run("ROLLBACK");
    throw err;
  }
}

export function createRepository(db: Database, persist: Persist): Repository {
  return {
    load: () => loadSnapshot(db),
    save: async (snapshot) => {
      writeSnapshot(db, snapshot);
      await persist(db.export());
    },
    exportBytes: () => db.export(),
  };
}

// --- helpers -------------------------------------------------------------

type Row = Record<string, unknown>;

function rows(db: Database, sql: string): Row[] {
  const res = db.exec(sql);
  const first = res[0];
  if (!first) return [];
  return first.values.map((tuple) => {
    const row: Row = {};
    first.columns.forEach((col, i) => {
      row[col] = tuple[i];
    });
    return row;
  });
}

function str(v: unknown): string {
  return typeof v === "string" ? v : String(v);
}

